/**
 * DropdownMenu helpers — refs, item query, portal placement.
 * @internal
 */

import type * as React from "react";
import type { DropdownMenuAlign, DropdownMenuSide } from "./DropdownMenu.types";

export function composeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> {
  return (node) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") ref(node);
      else (ref as React.MutableRefObject<T | null>).current = node;
    });
  };
}

export function childRefOf(
  child: React.ReactElement,
): React.Ref<HTMLElement> | undefined {
  const fromProps = (child.props as { ref?: React.Ref<HTMLElement> }).ref;
  if (fromProps) return fromProps;
  return (child as { ref?: React.Ref<HTMLElement> }).ref;
}

export function getEnabledItems(menu: HTMLElement | null): HTMLButtonElement[] {
  if (!menu) return [];
  return Array.from(
    menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)'),
  );
}

/**
 * Side for a submenu flyout: right unless the panel would overflow the viewport, then left.
 * When neither side fits, take the roomier one.
 */
export function resolveSubmenuSide(
  anchor: HTMLElement,
  content: HTMLElement,
  sideOffset: number,
): DropdownMenuSide {
  const rect = anchor.getBoundingClientRect();
  const needed = (content.offsetWidth || 224) + sideOffset;
  const margin = 8;
  const spaceRight = window.innerWidth - rect.right - margin;
  const spaceLeft = rect.left - margin;

  if (spaceRight >= needed) return "right";
  if (spaceLeft >= needed) return "left";
  return spaceRight >= spaceLeft ? "right" : "left";
}

export function computePosition(
  trigger: HTMLElement,
  content: HTMLElement,
  align: DropdownMenuAlign,
  sideOffset: number,
): { top: number; left: number } {
  const rect = trigger.getBoundingClientRect();
  const width = content.offsetWidth || 224;
  const height = content.offsetHeight || 0;
  const margin = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left =
    align === "end"
      ? rect.right - width
      : align === "center"
        ? rect.left + rect.width / 2 - width / 2
        : rect.left;
  left = Math.max(margin, Math.min(left, vw - width - margin));

  const below = rect.bottom + sideOffset;
  const above = rect.top - height - sideOffset;
  const spaceBelow = vh - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  const top =
    spaceBelow >= height || spaceBelow >= spaceAbove
      ? Math.min(below, vh - height - margin)
      : Math.max(margin, above);

  return { top, left };
}
